import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { noop } from 'lodash';
import { ReplaySubject, Subject } from 'rxjs';

import type {
  GoaltenderFormElementProps,
  GoaltenderFormElementValue,
} from '#components/integrations/GoaltenderFormElement';
import { GoaltenderFormElement } from '#components/integrations/GoaltenderFormElement';
import type {
  Conditions,
  DependsOn,
  EnvironmentFormVariables,
  LaunchDarklyIntegrationsManifest,
  VariableLocation,
} from '#types/generated/integrationSchema';
import type { Environment } from '#utils/environments/records/Environment';

// these must match the enums values for `dependeeKeyLocation` defined in the integration manifest's definitions.json document
type FormVariableType = VariableLocation;

type FormChangeEvent = { fieldKey: string; newValue: GoaltenderFormElementValue; formValues: FormValues };

type FormContextValue = {
  formValues: FormValues;
  formEventEmitter: Subject<FormChangeEvent>;
  triggerFormChangedEvent: (changedFormKey: string, newValue: GoaltenderFormElementValue) => void;
};

type FormContextProps = {
  manifest: LaunchDarklyIntegrationsManifest;
  environment: Environment;
  formVariableType: FormVariableType;
  children: ReactNode;
};

export type DynamicFormVariableProps = Omit<GoaltenderFormElementProps, 'onChange'> & {
  onChange(key: string[], _: GoaltenderFormElementValue): void;
};

type FormValues = { [key: string]: GoaltenderFormElementValue };

const getKeyName = (keyPrefix: FormVariableType, keyName: string) => `${keyPrefix}.${keyName}`;

/**
 * Takes the conditions for a form field and evalautes the conditions to determine if the conditions are met or not
 * @param conditions list of conditions to evaluate for a form field
 * @param value value to be used when evaluating conditions for a form field
 * @returns boolean result
 */
const isConditionsMet = (conditions: Conditions, value: GoaltenderFormElementValue) =>
  conditions.every((condition) => {
    if (!value) {
      return false;
    }

    switch (condition.operator) {
      case 'contains':
        return value.toString().toLowerCase().includes(condition.value.toLowerCase());
      case 'endsWith':
        return value.toString().endsWith(condition.value);
      case 'equalTo':
        return value === condition.value;
      case 'greaterThan':
        return +value > +condition.value;
      case 'greaterThanOrEqualTo':
        return +value >= +condition.value;
      case 'lessThan':
        return +value < +condition.value;
      case 'lessThanOrEqualTo':
        return +value <= +condition.value;
      case 'notEqual':
        return value !== condition.value;
      case 'startsWith':
        return value.toString().startsWith(condition.value);
      default:
        return false;
    }
  });

/**
 * Maps environment form variables keys to their set values from the environment service configuration
 * @param env project's environment
 * @param formVars integration manifest environment form variables
 * @returns map of form key and values
 */
const parseEnvironmentFormValues = (env?: Environment, formVars?: EnvironmentFormVariables): FormValues => {
  const values: FormValues = {};
  if (!formVars || !env) {
    return values;
  }

  for (const formVar of formVars) {
    values[getKeyName('environmentFormVariables', formVar.key)] =
      env.approvalSettings?.serviceConfig?.get(formVar.key) ?? undefined;
  }

  return values;
};

export const defaultContextValue: FormContextValue = {
  formValues: {},
  formEventEmitter: new Subject<FormChangeEvent>(),
  triggerFormChangedEvent: noop,
};

const IntegrationFormContext = createContext<FormContextValue>(defaultContextValue);

// Form context provider to orchestrate propagating form changes to child components
export function IntegrationFormContextProvider({
  manifest,
  environment,
  children,
  formVariableType,
}: FormContextProps) {
  const envFormVariables = manifest.capabilities?.approval?.environmentFormVariables;
  const envFormValues = parseEnvironmentFormValues(environment, envFormVariables);
  const [formValues, setFormValues] = useState<FormValues>(envFormValues);
  const formValuesRef = useRef(formValues);
  useLayoutEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  const formEventEmitter = useMemo(() => new ReplaySubject<FormChangeEvent>(10), []);

  const triggerFormChangedEvent = useCallback(
    (keyName: string, newValue: GoaltenderFormElementValue) => {
      const changedFormKey = getKeyName(formVariableType, keyName);
      const updatedFormValues = { ...formValuesRef.current, [changedFormKey]: newValue };
      formValuesRef.current = updatedFormValues;
      setFormValues(updatedFormValues);
      formEventEmitter.next({ fieldKey: changedFormKey, newValue, formValues: updatedFormValues });
    },
    [formEventEmitter, formVariableType],
  );

  const contextValue = useMemo(
    () => ({ formValues, formEventEmitter, triggerFormChangedEvent }),
    [formEventEmitter, formValues, triggerFormChangedEvent],
  );

  return <IntegrationFormContext.Provider value={contextValue}>{children}</IntegrationFormContext.Provider>;
}

function computeHideFromDependencies(formVar: DynamicFormVariableProps['formVar'], formValues: FormValues): boolean {
  let hide = formVar.isHidden ?? false;
  if (!formVar.dependsOn) {
    return hide;
  }

  for (const dep of formVar.dependsOn) {
    const conditionsMet = isConditionsMet(
      dep.conditions,
      String(formValues[getKeyName(dep.variableLocation, dep.variableKey)]),
    );
    const shouldHide = (conditionsMet && dep.action === 'hideField') || !!formVar.isHidden;
    const shouldShow = conditionsMet && dep.action === 'showField';
    if (shouldHide) {
      hide = true;
    }
    if (shouldShow) {
      hide = false;
    }
  }

  return hide;
}

export function DynamicGoaltenderFormElement({ formVar, onChange, ...props }: DynamicFormVariableProps) {
  const formContext = useContext(IntegrationFormContext);
  const { formEventEmitter, triggerFormChangedEvent, formValues } = formContext;
  const formValuesRef = useRef(formValues);
  useLayoutEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  const hide = useMemo(() => computeHideFromDependencies(formVar, formValues), [formVar, formValues]);

  const handleFormChanged = useCallback(
    (keyPath: string[], value: GoaltenderFormElementValue) => {
      triggerFormChangedEvent(formVar.key, value);
      onChange(keyPath, value);
    },
    [formVar.key, onChange, triggerFormChangedEvent],
  );

  const resetHiddenFieldValue = useCallback(
    (event: 'formInit' | 'formChanged', currentFormValues: FormValues, changedFieldKey?: string) => {
      if (!formVar.dependsOn) {
        return;
      }

      let dependencies: DependsOn = formVar.dependsOn;
      if (event === 'formChanged') {
        dependencies = formVar.dependsOn.filter(
          (v) => getKeyName(v.variableLocation, v.variableKey) === changedFieldKey,
        );
      }

      for (const dep of dependencies) {
        const conditionsMet = isConditionsMet(
          dep.conditions,
          String(currentFormValues[getKeyName(dep.variableLocation, dep.variableKey)]),
        );
        const shouldHide = (conditionsMet && dep.action === 'hideField') || !!formVar.isHidden;
        if (shouldHide) {
          handleFormChanged(['config', formVar.key], formVar.defaultValue ?? undefined);
        }
      }
    },
    [formVar.defaultValue, formVar.dependsOn, formVar.isHidden, formVar.key, handleFormChanged],
  );

  const onDependencyFormChanged = useEffectEvent((event: FormChangeEvent) => {
    resetHiddenFieldValue('formChanged', event.formValues, event.fieldKey);
  });

  const applyInitialValue = useEffectEvent(() => {
    resetHiddenFieldValue('formInit', formValuesRef.current);

    if (formVar.type === 'enum') {
      const initialEnumValue =
        props.value || formVar.defaultValue || (formVar.allowedValues && formVar.allowedValues[0]);
      triggerFormChangedEvent(formVar.key, initialEnumValue);
      if (props.value === undefined) {
        onChange(['config', formVar.key], initialEnumValue);
      }
      return;
    }
    triggerFormChangedEvent(formVar.key, props.value || formVar.defaultValue);
    if (props.value === undefined && formVar.defaultValue) {
      onChange(['config', formVar.key], formVar.defaultValue);
    }
  });

  const subscribeToFormChanges = useEffectEvent(() => {
    const subscription = formEventEmitter.subscribe((event: FormChangeEvent) => {
      onDependencyFormChanged(event);
    });
    return () => subscription.unsubscribe();
  });

  useEffect(() => subscribeToFormChanges(), []);

  const publishLoadedValue = useEffectEvent(() => {
    if (formVar.type === 'enum') {
      triggerFormChangedEvent(
        formVar.key,
        props.value || formVar.defaultValue || (formVar.allowedValues && formVar.allowedValues[0]),
      );
      return;
    }
    triggerFormChangedEvent(formVar.key, props.value || formVar.defaultValue);
  });

  const didRunInitialApply = useRef(false);
  const didApplyDefinedValue = useRef(false);

  useEffect(() => {
    const isFirst = !didRunInitialApply.current;
    const becameDefined = !didApplyDefinedValue.current && props.value !== undefined;
    didRunInitialApply.current = true;
    if (props.value !== undefined) {
      didApplyDefinedValue.current = true;
    }
    if (isFirst) {
      applyInitialValue();
    } else if (becameDefined) {
      publishLoadedValue();
    }
  }, [props.value]);

  if (hide) {
    return <></>;
  }

  return (
    <GoaltenderFormElement
      key={formVar.key}
      formVar={formVar}
      onChange={handleFormChanged}
      value={props.value}
      disabled={props.disabled}
      integrationKey={props.integrationKey}
      isRestricted={props.isRestricted}
      dynamicEnumOptions={props.dynamicEnumOptions}
      formState={props.formState}
    />
  );
}
